import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn, dayjs, deviceDetector } from "utils";
import { useMemo } from "react";
import { useSnapshot } from "valtio/react";
import { TrackerReportItem } from "../CopilotAgent/TimeLineItem";
import { setCopilotSenderChatRefContent } from "../CopilotAgent/CopilotSender";
import { AnalyzePortfolioModalContentProps } from "./types";
import Translations from "@translate/components/CoPilot/AnalyzePortfolioModal/AnalyzePortfolioModalContent";
import { I18nUtil } from "@utils/i18n";
import {
  useAgentDailyReport,
  useIsPc,
  useOpenChatModalWithClose,
  useRecommendQuestion,
} from "@/src/hooks";
import { SCOPE_MAP } from "@/src/const";
import { rootStore } from "@/src/store";
import { getRandomElements } from "@/src/utils";

const I18n = I18nUtil.createScoped(Translations);

export const AnalyzePortfolioModalContent = ({
  onClose,
  type = "DAILY_REPORT",
}: AnalyzePortfolioModalContentProps) => {
  const { data: dailyReport, isLoading } = useAgentDailyReport(
    type === "DAILY_REPORT" ? "0" : "99",
  );
  const { handleOpenChatModal } = useOpenChatModalWithClose();
  const { ownerInfo } = useSnapshot(rootStore.user);

  const mobileMode = deviceDetector.isMobile();
  const scopeKey = SCOPE_MAP.AGENT_SEARCH;
  const requestParams = {
    [`scopes[${scopeKey}]`]: 20,
    related: ownerInfo?.id as any,
  };
  const { questions } = useRecommendQuestion(requestParams, {
    key: scopeKey,
    staleTime: 1000 * 60 * 15,
  });
  const [isPc] = useIsPc();
  const summary = useMemo(() => {
    if (!type) {
      return {
        created: undefined,
        source_agent_ids: undefined,
        id: undefined,
        summary: undefined,
        isLoading: true,
      };
    }
    return {
      created: dailyReport?.agent_report?.created,
      source_agent_ids: dailyReport?.agent_report?.source_agent_ids,
      id: dailyReport?.agent_report?.id,
      isLoading,
      summary:
        dailyReport?.status === "GENERATABLE"
          ? undefined
          : dailyReport?.agent_report?.content?.summary,
    };
  }, [type, dailyReport, isLoading]);

  const handleChatClick = () => {
    const question = I18n.t("Give me more info about this report");
    const title = I18n.t("Portfolio Tracker %{var0}", {
      var0: dayjs(summary?.created ?? Date.now()).format("MMM DD YYYY"),
    });
    const newsTitle = I18n.t("Market Movers %{var0}", {
      var0: dayjs(summary?.created ?? Date.now()).format("MMM DD YYYY"),
    });
    if (isPc) {
      handleOpenChatModal({
        question: `[${type === "SUMMARY_NEWS" ? newsTitle : title}](referenceId=${summary?.id})\n${question}`,
        questions: getRandomElements(questions || [], 6),
      });
    } else {
      setCopilotSenderChatRefContent({
        type: "SUMMARY",
        link: "",
        title: type === "SUMMARY_NEWS" ? newsTitle : title,
        refId: summary?.id ?? "",
        question: question,
        desc: "",
        autoSubmit: true,
      });
    }
    onClose?.();
  };

  return (
    <div className="flex flex-col no-scrollbar h-full">
      <TrackerReportItem
        type="modal"
        summary={summary?.summary ?? ""}
        created={summary?.created ?? Date.now()}
        id={summary?.id ?? ""}
        isExpanded={true}
        reportType={type}
        modalParams={{
          onClose,
          isStatusLoading: summary?.isLoading,
        }}
      />
      <div
        className={cn(
          "flex flex-row w-full justify-between items-center bg-background_solid_secondary pt-4 gap-1 md:justify-end",
        )}
      >
        <div
          data-openreplay-label="AnalyzePortfolioModal · AnalyzePortfolioModalContent · div"
          className={cn(
            "button button_m md_button primary_button md_button w-full md:max-w-36.5",
            {
              "mb-safe": mobileMode,
            },
          )}
          onClick={handleChatClick}
        >
          <Icon
            name="p_chat"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
          />
          <span className=" text-content_inverse_primary">{I18n.t("Ask")}</span>
        </div>
      </div>
    </div>
  );
};
